import { ReactNode, useEffect, useMemo } from 'react';
import Image from 'next/image';
import { useHydrateAtoms } from 'jotai/utils';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { closeAllToasts, showToast } from '@restworld/ui-ds-base';
import { cn, getAssetSrc, useAnalytics } from '@restworld/utils-common';
import { useAuth } from '@restworld/feature-authentication';
import { useAccountsService } from '@restworld/data-services';

import ToastMessage from '../toast-message';
import { PosizionePageProps } from './job-position.types';
import JobPositionLayout from './job-position-layout';
import { useJobPositionClientData, useSetActiveStatus } from './use-effect-hooks';
import { JobPositionComponent } from './job-position-page-content';
import JobAlreadyApplied from '../job-already-applied';
import { JobStatusInfo } from '../job-status-info';
import {
  jobActiveStatusAtom,
  jobPositionApiDataAtom,
  jobPositionApplyStatusAtom,
  restworldGoogleRatingDataAtom,
} from './job-position.atoms';
import WorkerEntryPointModal from '../worker-entry-point/worker-entry-point-modal';
import WorkerManagedUsingKlaaryo, { workerManagedUsingKlaaryoModalFlagAtom } from '../worker-managed-using-klaaryo';
import UpdatePhoneNumberModal from '../common-phone-number-otp-validation-modal/update-phone-number-modal';
import OTPValidationModal from '../common-phone-number-otp-validation-modal/otp-validation-modal';
import { useApplyJob, useFetchWorkerDataQuery, useJobPositionId } from '../../hooks';
import { JobExplorerSaveResearchV2 } from '../job-explorer/job-explorer-map/job-explorer-save-research-v2';
import { domainInterestAtom } from '../job-explorer/job-explorer-server.atoms';
import { JobNotFoundPage } from '../legacy/job-not-found-page';

export const JobPositionPage = (props: PosizionePageProps) => {
  return (
    <JobPositionLayout jobPositionStatic={props.jobPositionStatic}>
      {props.errorCode || !props.jobPositionStatic ? (
        <JobNotFoundPage
          className={'pt-28'}
          icon={<Image width={320} height={324} src={getAssetSrc('/utils-assets/space-discovery.svg')} alt="" />}
        />
      ) : (
        <JobPositionContent {...props} />
      )}
    </JobPositionLayout>
  );
};

const JobPositionContent = (props: PosizionePageProps) => {
  const { jobPositionStatic, slug: id, restworldGoogleRatingData, jobActiveStatus, domainInterest } = props;
  const { user } = useAuth();
  const jobPositionId = useJobPositionId();
  const accServices = useAccountsService();
  const { trackEvent } = useAnalytics();
  useSetActiveStatus({ jobActiveStatus });

  useEffect(() => {
    if (!jobActiveStatus && jobPositionStatic?.id) {
      trackEvent('worker_viewed_offline_job_position', {
        job_position_id: jobPositionStatic.id,
        slug: id,
        job_title: jobPositionStatic.job_title,
        restaurant_id: jobPositionStatic.restaurant_id,
      });
    }
  }, [
    jobActiveStatus,
    jobPositionStatic?.id,
    jobPositionStatic?.job_title,
    jobPositionStatic?.restaurant_id,
    id,
    trackEvent,
  ]);
  const { jobPositionData } = useJobPositionClientData(jobPositionStatic, id);
  useFetchWorkerDataQuery();
  const { t } = useTranslation();
  useHydrateAtoms([
    [domainInterestAtom, domainInterest],
    [jobPositionApiDataAtom, jobPositionData],
    [restworldGoogleRatingDataAtom, restworldGoogleRatingData],
    [jobActiveStatusAtom, jobActiveStatus],
  ]);

  const setJpData = useSetAtom(jobPositionApiDataAtom);

  useEffect(() => {
    if (jobPositionData) setJpData(jobPositionData);
  }, [jobPositionData, setJpData]);

  const isEmployerWhoOwnsTheJp = useMemo(
    () => user?.id && user.id === jobPositionData?.restaurant?.employer_id,
    [jobPositionData?.restaurant?.employer_id, user?.id]
  );
  useEffect(() => {
    closeAllToasts();
    if (isEmployerWhoOwnsTheJp) {
      showToast(
        `${user?.name}, ${t('here_is_a_preview_of_how_candidates_view_your_job_posting_on_the_restworld_site')}`,
        'reversed'
      );
    }
  }, [isEmployerWhoOwnsTheJp, t, user?.name]);

  const setJobPositionApplyStatus = useSetAtom(jobPositionApplyStatusAtom);
  const setApplyConfirmationVisible = useSetAtom(workerManagedUsingKlaaryoModalFlagAtom);
  // Without this callback the OTP flow created the application but left the screen
  // unchanged: no confirmation, and the CTA still reading "Candidati ora". A changed
  // CTA on its own is a weak signal: people read it as "nothing happened", so open
  // the same confirmation modal the entry-point flow already shows.
  const { applyJobPosition } = useApplyJob({
    referralCode: useRouter()?.query?.['scout'],
    onAuthenticated: () => {
      setJobPositionApplyStatus(true);
      setApplyConfirmationVisible(true);
    },
  });
  return (
    <>
      <ToastMessage />
      <ComponentWrapper>
        <JobPositionComponent />
      </ComponentWrapper>
      <JobStatus />
      <JobAlreadyApplied />
      <WorkerEntryPointModal />
      <WorkerManagedUsingKlaaryo />
      <UpdatePhoneNumberModal
        headerTitle={t('common:verify_your_phone_number')}
        headerSubTitle={t('common:verify_phone_number_message')}
      />
      <OTPValidationModal
        onOTPVerificationSuccessFull={async () => {
          try {
            await accServices.me();
          } catch (error) {
            console.error(error, 'error on refetching me data');
          }
          applyJobPosition({ workerIdArg: user?.id, jobPositionId });
        }}
      />
      <JobExplorerSaveResearchV2
        prefillValues={{
          address: {
            address: jobPositionData?.restaurant?.address,
            address_lat: jobPositionData?.restaurant?.address_lat,
            address_lon: jobPositionData?.restaurant?.address_lon,
            country: jobPositionData?.restaurant?.country,
            locality: jobPositionData?.restaurant?.locality,
            postal_code: jobPositionData?.restaurant?.postal_code,
            administrative_area_level_1: jobPositionData?.restaurant?.administrative_area_level_1,
            administrative_area_level_2: jobPositionData?.restaurant?.administrative_area_level_2,
          },
          // Default radius: 20 km matches the same constraint we use in
          // the JP-similarity LOCAL branch (`SimilarityCriteria.local_radius_meters`),
          // so the user's saved search aligns with what they'd see as
          // "similar offers" if they came back to an offline JP. For
          // NATIONAL JPs the wizard's caller switches `areaSelectionAtom`
          // to "whole_italy" before opening, making this value moot.
          area_distance: 20,
          email: user?.email,
          domain_interest: jobPositionData?.domain_interest ? [jobPositionData?.domain_interest] : [],
          // FilterSelection expects a domain→[occupation_ids] map. When the
          // origin JP has no `domain_interest` we fall back to a synthetic
          // `_unknown` key so the occupation prefill still reaches the form.
          // Submit-side flattening (`Object.values(...).flat()` in
          // `job-explorer-save-research-v2.tsx`) ignores the keys and sends
          // every occ_id regardless, so `_unknown` is harmless at the API
          // level. Edge case: if the user later picks a domain inside the
          // wizard, `OccupationSelect` reads `occupation_id.<picked-domain>`,
          // which is empty, and the `_unknown`-keyed prefill becomes
          // orphaned in form state (still sent on submit, just not visible
          // in the picked-domain occupation list).
          occupation_id: jobPositionData?.occupation_id
            ? {
                [jobPositionData?.domain_interest ?? '_unknown']: [String(jobPositionData.occupation_id)],
              }
            : undefined,
        }}
        startIndex={0}
      />
    </>
  );
};

const ComponentWrapper = ({ children }: { children: ReactNode }) => (
  <MainWrapper>
    <Container>
      <SubContainer>{children}</SubContainer>
    </Container>
  </MainWrapper>
);
const JobStatus = () => {
  const jobPositionApiData = useAtomValue(jobPositionApiDataAtom);
  return <JobStatusInfo jobPositionApiData={jobPositionApiData} />;
};

const MainWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-full h-full', className)} {...props} />
);
const Container = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('lg:rounded-none lg:shadow-none', className)} {...props} />
);
const SubContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('md:flex md:justify-between', className)} {...props} />
);
